'use client';

import { createContext, useContext, useEffect, useState } from 'react';

export type ColorTheme = 'emerald' | 'ocean' | 'purple' | 'rose' | 'amber';

const STORAGE_KEY = '@healthvox_color_theme';

const ColorThemeContext = createContext<{
  colorTheme: ColorTheme;
  setColorTheme: (theme: ColorTheme) => void;
}>({
  colorTheme: 'emerald',
  setColorTheme: () => {},
});

export function ColorThemeProvider({ children }: { children: React.ReactNode }) {
  const [colorTheme, setColorThemeState] = useState<ColorTheme>('emerald');

  useEffect(() => {
    const saved = localStorage.getItem(STORAGE_KEY) as ColorTheme | null;
    if (saved && ['emerald', 'ocean', 'purple', 'rose', 'amber'].includes(saved)) {
      setColorThemeState(saved);
      document.documentElement.className = document.documentElement.className
        .replace(/theme-\w+/g, '')
        .trim();
      if (saved !== 'emerald') {
        document.documentElement.classList.add(`theme-${saved}`);
      }
    }
  }, []);

  const setColorTheme = (theme: ColorTheme) => {
    setColorThemeState(theme);
    localStorage.setItem(STORAGE_KEY, theme);
    document.documentElement.className = document.documentElement.className
      .replace(/theme-\w+/g, '')
      .trim();
    if (theme !== 'emerald') {
      document.documentElement.classList.add(`theme-${theme}`);
    }
  };

  return (
    <ColorThemeContext.Provider value={{ colorTheme, setColorTheme }}>
      {children}
    </ColorThemeContext.Provider>
  );
}

export function useColorTheme() {
  return useContext(ColorThemeContext);
}
